// Record-on-action for the commission ledger (rep_commission_corrections).
//
// Called AFTER an invoice change (admin void, approved rep adjustment, approved
// rep void, Commission-tab Delete) has ALREADY succeeded in Supabase. It never
// blocks or undoes that change: a failure is returned as a message the UI shows
// as an amber/red note, and the "Correction needed" scan stays the backstop.
//
// It reads the invoice's CURRENT state fresh from portal_orders (never the
// SHARED_INVOICES cache), then delegates to recordCorrectionForInvoice, which
// itself re-reads the paid rows and existing corrections and applies the pure
// computeCorrection formula. Nothing paid is ever rewritten. When nothing was
// paid for the invoice there is nothing to record (status "none").
import { recordCorrectionForInvoice, getInvoiceStateForCorrection } from "./supabase";
import { currentAdminIdentity } from "./commissionLedgerUi";
import { fmtSignedMoney } from "./commissionCorrectionsView";

const defaultDeps = { recordCorrectionForInvoice, getInvoiceStateForCorrection, currentAdminIdentity };

export const CORRECTION_NOT_RECORDED_TAIL = "It will appear under Correction needed in the Commission tab.";

/**
 * @param {{invoiceId:string, reason:string, source:string, expect?:'void'|'adjust'}} p
 * @returns {Promise<{status:'none'|'recorded'|'failed', delta?:number, message:string}>} never throws
 */
export async function recordCorrectionAfterAction(p, deps = defaultDeps) {
  const d = { ...defaultDeps, ...deps };
  const { invoiceId, reason, source, expect, legacyPaid } = p || {};
  const failed = (why) => ({ status: "failed", detail: why, message: `Invoice ${invoiceId} was changed, but its commission correction was not recorded (${why}). ${CORRECTION_NOT_RECORDED_TAIL}` });
  try {
    if (!invoiceId) return failed("no invoice id");
    const st = await d.getInvoiceStateForCorrection(invoiceId);
    if (!st || !st.ok) return failed("could not read the invoice from the database: " + ((st && st.error) || "unknown error"));
    if (!st.found) return { status: "none", message: "" };
    if (expect === "void" && !st.cancelled) return failed("the database does not show the invoice as cancelled yet");
    if (!st.cancelled && !(Number.isFinite(st.total) && st.total >= 0)) return failed("the invoice total in the database is not a valid amount");
    const admin = await d.currentAdminIdentity();
    const res = await d.recordCorrectionForInvoice({
      invoiceId, newTotal: st.total, isVoid: !!st.cancelled,
      reason: reason || (st.cancelled ? "Invoice voided after payout" : "Invoice adjusted after payout"),
      source: source || null, admin, legacyPaid: legacyPaid === true,
    });
    if (res && res.ok && res.inserted) {
      const delta = Number(res.inserted.delta);
      return { status: "recorded", delta, message: `A commission correction of ${fmtSignedMoney(delta)} was recorded for the rep and will be netted into their next payout.` };
    }
    if (res && res.ok && res.none) return { status: "none", message: "" };
    if (res && res.refused) return failed(res.reason || "refused, needs a manual review");
    return failed((res && res.error) || "unknown error");
  } catch (e) {
    console.warn("[recordCorrectionAfterAction] threw for", invoiceId, ":", e?.message || e);
    return failed(e?.message || String(e));
  }
}
